import { Component, OnInit, OnDestroy } from '@angular/core';
import { State, SortDescriptor, process, aggregateBy, AggregateResult } from '@progress/kendo-data-query';
import { GridDataResult, DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { SharedService } from '../../services/shared.service';
import { VennHealthDataService } from '../../services/vennhealth-data.service';
import { environment } from '../../../environments/environment';
import { IndicationGroup } from '@app/models/indication';
import { FilterModel } from '@app/models/filter-model';
import { VennHealthDialogService } from '@app/services/vennhealth-dialog.service';
import { UTrackUIEvent } from '@app/models/utrack-ui-event';
import { AccountService } from '@app/services/account-service';

@Component({
  selector: 'app-results-grid',
  templateUrl: './results-grid.component.html',
  styleUrls: ['./results-grid.component.css']
})
export class ResultsGridComponent implements OnInit, OnDestroy {

  queryName: string;
  exportFileName = "";

  // variables
  loading = false;
  resultMessage = '';
  gridColumns: any[] = [];
  queryData: any[] = [];
  rawData: any[] = [];
  sumValues: any[] = [];
  gridData: GridDataResult;
  allIndications: IndicationGroup[] = [];
  selectedDataSets: any = [];
  filterModel: FilterModel;
  summarySectionInfoForExport: any = [];

  // grid sorting
  public gridSort: SortDescriptor[] = [{
    field: 'year',
    dir: 'asc'
  }];

  // grid state
  public gridState: State = {};

  isDemoUser = false;

  constructor(
    private readonly sharedService: SharedService,
    private readonly vennHealthDataService: VennHealthDataService,
    private readonly vennHealthDiaglogService: VennHealthDialogService,
    private readonly accountService: AccountService
  ) {
    this.allExportData = this.allExportData.bind(this);
  }

  ngOnInit() {
    const account = this.accountService.accountValue;
    if (account) {
      this.isDemoUser = account.roles.includes(103);
    }

    this.sharedService.allIndications.subscribe(data => {
      if (data) {
        this.allIndications = data;
      }
    });

    this.sharedService.selectedDataSets.subscribe(data => {
      if (data) {
        this.selectedDataSets = data;
      }
    });

    this.sharedService.summarySectionInfo.subscribe(data => {
      if (data) {
        this.summarySectionInfoForExport = data;
      }
    });

    this.sharedService.activeQueryName.subscribe(
      queryName => {
        if (queryName !== null) {
          this.queryName = queryName;
        } else {
          //todo revisit this
          queryName = environment.defaultQueryName;
        }
      }
    );

    this.sharedService.runQuery.subscribe(
      run => {
        if (run !== null && run === true) {
          this.runQuery();
        }
      }
    );
  }

  public gridDataStateChange(state: DataStateChangeEvent): void {
    this.gridState = state;
    this.gridData = process(this.rawData, this.gridState);
  }

  gridSortChange(sort: SortDescriptor[]): void {
    this.gridSort = sort;
    this.gridState.sort = sort;
    this.loadGridData();
  }

  canExport() {
    return this.rawData.length > 0;
  }

  getExportFileName(type: string) {
    return `VennHealth-${this.queryName}-${type}-${new Date().toDateString()}.xlsx`;
  }

  onGridExport(e: any): void {
    const gridRows = e.workbook.sheets[0].rows;
    // blank row spacer between filters and data grid
    gridRows.unshift(
      {
        cells: [
            {
                value: ``,
                colSpan: 1,
                color: "#000000",
                rowSpan: 1
            }
        ]
      } 
    );
    // filter selections
    this.summarySectionInfoForExport.forEach(item => {
      gridRows.unshift(
        {
          cells: [
              {
                  value: item.indication,
                  colSpan: 1,
                  color: "#000000",
                  rowSpan: 1
              },
              {
                value: item.details.toString().split(",").join("\r\n"),
                colSpan: 10,
                color: "#000000",
                rowSpan: 1
              }
          ]
        } 
      );
    });
    // header row for filters
    gridRows.unshift(
      {
        cells: [
            {
                value: `Indication`,
                background: "#2baab1",
                colSpan: 1,
                color: "#fff",
                rowSpan: 1
            },
            {
              value: `Details`,
              background: "#2baab1",
              colSpan: 10,
              color: "#fff",
              rowSpan: 1
          }
        ]
      } 
    );
  }

  allExportData(): ExcelExportData {
    const e: UTrackUIEvent = {
      details: 'Export',
      additionalDetails: 'Grid'
    };
    this.vennHealthDataService.utrackEvent(e).subscribe();

    this.exportFileName = this.getExportFileName('VennHealth_Export');
    const result: ExcelExportData =  {
        data: process(this.rawData, {filter: this.gridState.filter, sort: this.gridSort}).data
    };

    return result;
  }

  loadGridData(): void {
    this.gridData = process(this.rawData, this.gridState);
  }

  runQuery() {
    const selectedIndicationsCount = this.getSelectedIndicationsCount();
    if (this.isDemoUser && selectedIndicationsCount > 2) {
      this.vennHealthDiaglogService.showNotification(`You have selected ${selectedIndicationsCount} indications which exceeds the limit of 2 per demo query. Please reduce your selected indications to 2 or fewer.`, {style: 'info', icon: true }, 10000);
    } else if (selectedIndicationsCount > 5) {
      this.vennHealthDiaglogService.showNotification(`You have selected ${selectedIndicationsCount} indications which exceeds the limit of 5 per query. Please reduce your selected indications to 5 or fewer.`, {style: 'info', icon: true }, 10000);
    } else {
      this.resultMessage = '';
      this.loading = true;
      this.rawData = [];
      this.sumValues = [];
      this.filterModel = {
        queryName: this.queryName,
        startDate: new Date(),
        dataSets: this.selectedDataSets,
        indicationGroups: this.allIndications
      };

      //this.searchSelections.icd10CodeSelections = this.currentIcdCodeSelections;
      // used by results-status component to determine if the warning of changed selections should appear
      // this.sharedService.setLastRanIcdCodeSelections(this.currentIcdCodeSelections);

      // log the query being run
      this.vennHealthDataService.logExecutedQueryStart(this.filterModel).subscribe(
        data => {
          this.sharedService.setRefreshQueryHistory();
        }
      );

      // run the query
      this.vennHealthDataService.runQuery(this.filterModel).subscribe({
        next: (v) => {
          console.log(v);
          this.formatQueryResults(v);
        },
        error: (e) => {
          console.error(e);
          this.loading = false;
        },
        complete: () => {
          console.info('complete');
          this.loading = false;
        }
      });
    }
  }

  getSelectedIndicationsCount(): number {
    var count = 0;
    this.allIndications.forEach(group => {
      group.indications.forEach(indication => {
        if (indication.selected) {
          count++;
        }
      });
    })

    return count;
  }

  formatQueryResults(data) {
    this.loading = false;

    if (data !== null) {
      if (data.errorMessage === null) {
        this.rawData = data.pivotTableData;
        this.sharedService.setRawResultsData(
          {
            queryResults: data.indicationResults,
            pivotTableData: data.pivotTableData,
            selectedFilters: data.selectedFilters,
            selectedDataSets: data.selectedDataSets,
            yearsList: data.headers
          });
        this.gridColumns = data.headers.map(i => ({ field: 'col_' + i, title: i}));
        this.loadGridData();
      } else if (data.errorMessage !== null) {
        // set this so that the chart can get access to it and decide to hide the chart
        this.sharedService.setRawResultsData(
          {
            queryResults: [],
            pivotTableData: [],
            resultMessage: data.errorMessage,
            selectedFilters: [],
            selectedDataSets: [],
            yearsList: []
          });
        this.resultMessage = data.errorMessage;
      }
    }
  }

  sumGridFooter(columnName: string) {
    let aggregateResult: AggregateResult;
    let formattedSum;
    aggregateResult = aggregateBy(this.rawData, [{ aggregate: 'sum', field: columnName }]);
    formattedSum = this.numberWithCommas(aggregateResult[columnName].sum);
    this.sumValues.push({ [columnName]: formattedSum });
    return formattedSum;
  }

  numberWithCommas(x: number) {
    return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  ngOnDestroy(): void {
  }

}
